import React, { useState, useCallback, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useDropzone } from 'react-dropzone'
import { X, Upload, Trash2, CheckCircle, AlertCircle, Star, Image as ImageIcon, Loader, RotateCw, Eye } from 'lucide-react'
import GlassCard from './ui/GlassCard'
import GradientButton from './ui/GradientButton'
import api from '@/services/api'
import { useTranslation } from 'react-i18next'

interface ImageUploadModalProps {
  isOpen: boolean
  onClose: () => void
  propertyId: string
  propertyTitle: string
  existingImages?: string[]
  featuredImage?: string
  onUploadComplete: () => void
  readOnly?: boolean
}

interface PreviewImage {
  file: File
  preview: string
  id: string
  rotation: number
}

interface ExistingImage {
  url: string
  index: number
  isFeatured: boolean
}

interface PreviewModalState {
  open: boolean
  imageUrl: string
  imageIndex: number
  rotation: number
  saving: boolean
  isNewImage: boolean
  newImageId: string
}

const rotateImageBlob = (file: File, degrees: number): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    img.onload = () => {
      const canvas = document.createElement('canvas')
      const rad = (degrees * Math.PI) / 180
      const sin = Math.abs(Math.sin(rad))
      const cos = Math.abs(Math.cos(rad))
      canvas.width = img.width * cos + img.height * sin
      canvas.height = img.width * sin + img.height * cos
      const ctx = canvas.getContext('2d')!
      ctx.translate(canvas.width / 2, canvas.height / 2)
      ctx.rotate(rad)
      ctx.drawImage(img, -img.width / 2, -img.height / 2)
      URL.revokeObjectURL(url)
      canvas.toBlob((blob) => {
        if (blob) resolve(blob)
        else reject(new Error('Error al procesar imagen'))
      }, 'image/jpeg', 0.92)
    }
    img.onerror = () => reject(new Error('Error al cargar imagen'))
    img.src = url
  })
}

const ImageUploadModal: React.FC<ImageUploadModalProps> = ({
  isOpen,
  onClose,
  propertyId,
  propertyTitle,
  existingImages = [],
  featuredImage = '',
  onUploadComplete,
  readOnly = false
}) => {
  const { t } = useTranslation('properties')
  const [previewImages, setPreviewImages] = useState<PreviewImage[]>([])
  const [currentImages, setCurrentImages] = useState<ExistingImage[]>([])
  const [deleting, setDeleting] = useState<number | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState('')
  const [planLimits, setPlanLimits] = useState({ max: 10, current: 0 })
  const [previewModal, setPreviewModal] = useState<PreviewModalState>({
    open: false, imageUrl: '', imageIndex: -1, rotation: 0, saving: false, isNewImage: false, newImageId: ''
  })

  useEffect(() => {
    if (isOpen) {
      loadExistingImages()
      fetchPlanLimits()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, propertyId])

  const loadExistingImages = () => {
    setCurrentImages(existingImages.map((url, index) => ({
      url, index, isFeatured: url === featuredImage
    })))
  }

  const fetchPlanLimits = async () => {
    try {
      const response = await api.get('/api/agents/plan-limits')
      const { limits } = response.data
      setPlanLimits({ max: limits.max_images_per_property, current: existingImages.length })
    } catch (error) {
      setPlanLimits({ max: 1, current: existingImages.length })
    }
  }

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (readOnly) return
    const availableSlots = planLimits.max === -1 ? 100 : planLimits.max - currentImages.length - previewImages.length
    if (availableSlots <= 0) {
      setUploadStatus('error')
      setErrorMessage(t('imgLimitReached', { max: planLimits.max }))
      return
    }
    const filesToAdd = acceptedFiles.slice(0, availableSlots)
    if (filesToAdd.length < acceptedFiles.length) {
      setUploadStatus('error')
      setErrorMessage(t('imgCanAddMore', { count: availableSlots }))
    }
    const newImages = filesToAdd.map(file => ({
      file, preview: URL.createObjectURL(file),
      id: Math.random().toString(36).substr(2, 9), rotation: 0
    }))
    setPreviewImages(prev => [...prev, ...newImages])
    if (filesToAdd.length === acceptedFiles.length) { setUploadStatus('idle'); setErrorMessage('') }
  }, [currentImages.length, previewImages.length, planLimits, readOnly])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'] },
    maxSize: 10 * 1024 * 1024,
    disabled: uploading || deleting !== null || readOnly,
    onDropRejected: (rejectedFiles) => {
      const error = rejectedFiles[0]?.errors[0]
      if (error?.code === 'file-too-large') {
        setUploadStatus('error')
        setErrorMessage(t('imgTooLarge'))
      }
    },
  })

  // Abrir preview modal para imagen existente
  const openPreviewExisting = (image: ExistingImage) => {
    setPreviewModal({ open: true, imageUrl: image.url, imageIndex: image.index, rotation: 0, saving: false, isNewImage: false, newImageId: '' })
  }

  // Abrir preview modal para imagen nueva
  const openPreviewNew = (image: PreviewImage) => {
    setPreviewModal({ open: true, imageUrl: image.preview, imageIndex: -1, rotation: image.rotation, saving: false, isNewImage: true, newImageId: image.id })
  }

  // Rotar en el modal de preview
  const rotateInPreview = () => {
    setPreviewModal(prev => ({ ...prev, rotation: (prev.rotation + 90) % 360 }))
  }

  // Guardar rotación de imagen existente — llama al backend
  const saveRotationExisting = async () => {
    setPreviewModal(prev => ({ ...prev, saving: true }))
    try {
      const response = await api.patch(
        `/api/agents/properties/${propertyId}/images/${previewModal.imageIndex}/rotate`
      )
      if (response.data.success && response.data.newUrl) {
        const newUrl = response.data.newUrl
        // Actualizar currentImages en estado local con nueva URL y nueva rotación visual
        setCurrentImages(prev => prev.map(img =>
          img.index === previewModal.imageIndex
            ? { ...img, url: newUrl, isFeatured: img.isFeatured }
            : img
        ))
        setPreviewModal(prev => ({ ...prev, open: false, saving: false, imageUrl: newUrl, rotation: 0 }))
        onUploadComplete()
        setUploadStatus('idle')
      }
    } catch (error: any) {
      setPreviewModal(prev => ({ ...prev, saving: false, open: false }))
      setUploadStatus('error')
      setErrorMessage(error?.response?.data?.error || t('imgErrorRotate'))
    }
  }

  // Guardar rotación de imagen nueva — actualiza rotation en previewImages
  const saveRotationNew = () => {
    setPreviewImages(prev => prev.map(img =>
      img.id === previewModal.newImageId ? { ...img, rotation: previewModal.rotation } : img
    ))
    setPreviewModal(prev => ({ ...prev, open: false }))
  }

  const removePreviewImage = (id: string) => {
    if (readOnly) return
    setPreviewImages(prev => {
      const toRemove = prev.find(img => img.id === id)
      if (toRemove) URL.revokeObjectURL(toRemove.preview)
      return prev.filter(img => img.id !== id)
    })
    setUploadStatus('idle'); setErrorMessage('')
  }

  const deleteExistingImage = async (index: number) => {
    if (readOnly) return
    if (!confirm(t('imgConfirmDelete'))) return
    setDeleting(index)
    try {
      await api.delete(`/api/agents/properties/${propertyId}/images/${index}`)
      onUploadComplete()
      setCurrentImages(prev => prev.filter(img => img.index !== index))
    } catch (error: any) {
      setUploadStatus('error')
      setErrorMessage(error.response?.data?.error || t('imgErrorDelete'))
    } finally {
      setDeleting(null)
    }
  }

  const setFeaturedImage = async (url: string) => {
    if (readOnly) return
    try {
      await api.patch(`/api/agents/properties/${propertyId}/featured`, { featured_image: url })
      setCurrentImages(prev => prev.map(img => ({ ...img, isFeatured: img.url === url })))
      onUploadComplete()
    } catch (error: any) {
      setUploadStatus('error')
      setErrorMessage(error.response?.data?.error || t('imgErrorFeatured'))
    }
  }

  const handleUpload = async () => {
    if (readOnly || previewImages.length === 0) return
    setUploading(true); setUploadProgress(0); setUploadStatus('idle'); setErrorMessage('')
    try {
      const formData = new FormData()
      for (const img of previewImages) {
        if (img.rotation !== 0) {
          const rotatedBlob = await rotateImageBlob(img.file, img.rotation)
          formData.append('images', new File([rotatedBlob], img.file.name, { type: 'image/jpeg' }))
        } else {
          formData.append('images', img.file)
        }
      }
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => prev < 90 ? prev + (prev < 30 ? 5 : prev < 60 ? 3 : 2) : prev)
      }, 100)
      const response = await api.post(`/api/agents/properties/${propertyId}/images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000
      })
      clearInterval(progressInterval)
      setUploadProgress(100)
      if (response.status === 200 && response.data?.success) {
        setUploadStatus('success')
        const nuevasUrls: string[] = response.data.imageUrls || []
        setCurrentImages(prev => [
          ...prev,
          ...nuevasUrls.map((url, i) => ({ url, index: prev.length + i, isFeatured: false }))
        ])
        setTimeout(() => { onUploadComplete(); setPreviewImages([]); setUploadProgress(0); setUploadStatus('idle') }, 1500)
      } else {
        throw new Error(response.data?.error || t('imgErrorUnknown'))
      }
    } catch (error: any) {
      setUploadProgress(0); setUploadStatus('error')
      setErrorMessage(error.response?.data?.error || error.request ? t('imgErrorConnection') : error.message || t('imgErrorUpload'))
      console.error('Upload error:', error)
    } finally {
      setUploading(false)
    }
  }

  const handleClose = () => {
    previewImages.forEach(img => URL.revokeObjectURL(img.preview))
    setPreviewImages([]); setUploadProgress(0); setUploadStatus('idle'); setErrorMessage('')
    onClose()
  }

  if (!isOpen) return null
  const totalImages = currentImages.length + previewImages.length

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={(e) => e.target === e.currentTarget && !previewModal.open && handleClose()}
      >
        {/* Preview / Rotate Modal */}
        {previewModal.open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
            className="absolute inset-0 bg-black/80 z-60 flex items-center justify-center p-4"
            style={{ zIndex: 60 }}
          >
            <div className="bg-slate-900 rounded-2xl p-6 max-w-2xl w-full shadow-2xl border border-white/10">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-white font-bold text-lg">{t('imgPreview')}</h3>
                <button onClick={() => setPreviewModal(prev => ({ ...prev, open: false }))}
                  className="p-2 hover:bg-white/10 rounded-lg text-white/60 hover:text-white transition-colors">
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Imagen con rotación visual */}
              <div className="flex items-center justify-center bg-black/30 rounded-xl overflow-hidden mb-6" style={{ height: '400px' }}>
                <img
                  src={previewModal.imageUrl}
                  alt="Preview"
                  className="max-w-full max-h-full object-contain transition-transform duration-300"
                  style={{ transform: `rotate(${previewModal.rotation}deg)` }}
                />
              </div>

              {/* Controles */}
              <div className="flex items-center justify-between gap-4">
                <button
                  onClick={rotateInPreview}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white transition-colors"
                >
                  <RotateCw className="h-4 w-4" />
                  {t('imgRotate90')}
                  {previewModal.rotation !== 0 && <span className="text-xs bg-blue-700 px-2 py-0.5 rounded">{previewModal.rotation}°</span>}
                </button>

                <div className="flex gap-3">
                  <button
                    onClick={() => setPreviewModal(prev => ({ ...prev, open: false }))}
                    className="px-4 py-2 border border-white/20 text-white rounded-lg hover:bg-white/10 transition-colors"
                  >
                    {t('imgCancel')}
                  </button>
                  {previewModal.rotation !== 0 && (
                    <button
                      onClick={previewModal.isNewImage ? saveRotationNew : saveRotationExisting}
                      disabled={previewModal.saving}
                      className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 rounded-lg text-white transition-colors disabled:opacity-50"
                    >
                      {previewModal.saving ? <Loader className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                      {t('imgSave')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        >
          <GlassCard>
            {/* Header */}
            <div className="flex justify-between items-start mb-6">
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-white mb-1">{readOnly ? t('imgViewTitle') : t('imgManageTitle')}</h2>
                <p className="text-white/60 text-sm">{propertyTitle}</p>
                <div className="mt-2 flex items-center gap-4 text-sm">
                  <span className="text-white/70"><ImageIcon className="inline h-4 w-4 mr-1" />{t('imgCount', { count: currentImages.length })}</span>
                  {readOnly && <span className="text-yellow-400 text-xs">{t('imgReadOnly')}</span>}
                </div>
              </div>
              <button onClick={handleClose} className="text-white/60 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-lg" disabled={uploading || deleting !== null}>
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Existing Images */}
            {currentImages.length > 0 && (
              <div className="mb-6">
                <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                  <ImageIcon className="h-5 w-5" />
                  {readOnly ? t('imgImagesView', { count: currentImages.length }) : t('imgImagesCurrent', { count: currentImages.length })}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {currentImages.map((image) => (
                    <motion.div key={image.index} layout initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} className="relative group">
                      <div className="aspect-square rounded-lg overflow-hidden bg-white/10 border-2 border-white/20">
                        <img src={image.url} alt={`Imagen ${image.index + 1}`} className="w-full h-full object-cover" />
                      </div>
                      {image.isFeatured && (
                        <div className="absolute top-2 left-2 bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                          <Star className="h-3 w-3 fill-current" />{t('imgFeatured')}
                        </div>
                      )}
                      {!readOnly && (
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          {!image.isFeatured && (
                            <button onClick={() => setFeaturedImage(image.url)} className="p-2 bg-yellow-500 hover:bg-yellow-600 rounded-lg transition-colors" title={t('imgTooltipFeatured')}>
                              <Star className="h-4 w-4 text-white" />
                            </button>
                          )}
                          <button onClick={() => openPreviewExisting(image)} className="p-2 bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors" title={t('imgTooltipView')}>
                            <Eye className="h-4 w-4 text-white" />
                          </button>
                          <button onClick={() => deleteExistingImage(image.index)} className="p-2 bg-red-500 hover:bg-red-600 rounded-lg transition-colors" disabled={deleting === image.index} title={t('imgTooltipDelete')}>
                            {deleting === image.index ? <Loader className="h-4 w-4 text-white animate-spin" /> : <Trash2 className="h-4 w-4 text-white" />}
                          </button>
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {readOnly && currentImages.length === 0 && (
              <div className="text-center py-12">
                <ImageIcon className="h-16 w-16 mx-auto mb-4 text-white/30" />
                <p className="text-white/60">{t('imgNoImages')}</p>
              </div>
            )}

            {/* Upload Zone */}
            {!readOnly && (planLimits.max === -1 || totalImages < planLimits.max) && (
              <>
                <div {...getRootProps()} className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${isDragActive ? 'border-blue-400 bg-blue-500/10 scale-[1.02]' : 'border-white/20 hover:border-white/40 hover:bg-white/5'} ${uploading || deleting !== null ? 'pointer-events-none opacity-50' : ''}`}>
                  <input {...getInputProps()} />
                  <Upload className="h-12 w-12 text-white/40 mx-auto mb-4" />
                  <h3 className="text-white font-medium mb-2">{isDragActive ? t('imgDropHere') : t('imgDragOrClick')}</h3>
                  <p className="text-white/60 text-sm">{t('imgFormatsHint')}</p>
                </div>

                {previewImages.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-white font-medium mb-3 flex items-center gap-2"><Upload className="h-5 w-5" />{t('imgToUpload')} ({previewImages.length})</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {previewImages.map((image) => (
                        <div key={image.id} className="relative group">
                          <div className="aspect-square rounded-lg overflow-hidden bg-white/10 border-2 border-green-500/50">
                            <img src={image.preview} alt="Preview" className="w-full h-full object-cover transition-transform duration-300" style={{ transform: `rotate(${image.rotation}deg)` }} />
                          </div>
                          <button onClick={() => openPreviewNew(image)} className="absolute top-2 left-2 p-1.5 bg-blue-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg" disabled={uploading} title={t('imgTooltipView')}>
                            <Eye className="h-3 w-3 text-white" />
                          </button>
                          <button onClick={() => removePreviewImage(image.id)} className="absolute -top-2 -right-2 p-1.5 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg" disabled={uploading}>
                            <Trash2 className="h-3 w-3 text-white" />
                          </button>
                          <div className="absolute bottom-2 left-2 bg-green-500 text-white px-2 py-0.5 rounded text-xs font-semibold">{t('imgNew')}</div>
                          {image.rotation !== 0 && <div className="absolute bottom-2 right-2 bg-blue-500 text-white px-2 py-0.5 rounded text-xs font-semibold">{image.rotation}°</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {!readOnly && uploading && (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white text-sm font-medium">{t('imgUploading')}</span>
                  <span className="text-white text-sm font-bold">{uploadProgress}%</span>
                </div>
                <div className="w-full bg-white/20 rounded-full h-3 overflow-hidden">
                  <motion.div className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 h-3 rounded-full" initial={{ width: 0 }} animate={{ width: `${uploadProgress}%` }} transition={{ duration: 0.3 }} />
                </div>
              </div>
            )}

            <AnimatePresence>
              {uploadStatus === 'success' && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="mt-4 p-4 bg-green-500/20 border border-green-500/30 rounded-lg flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
                  <span className="text-green-400 text-sm font-medium">{t('imgSuccess')}</span>
                </motion.div>
              )}
              {uploadStatus === 'error' && errorMessage && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="mt-4 p-4 bg-red-500/20 border border-red-500/30 rounded-lg flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <span className="text-red-400 text-sm">{errorMessage}</span>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex gap-4 justify-end mt-6 pt-6 border-t border-white/10">
              <button onClick={handleClose} disabled={uploading || deleting !== null} className="px-6 py-3 rounded-xl border border-white/20 text-white hover:bg-white/10 transition-colors disabled:opacity-50">
                {readOnly ? t('imgClose') : (previewImages.length > 0 ? t('imgCancel') : t('imgClose'))}
              </button>
              {!readOnly && previewImages.length > 0 && (
                <GradientButton onClick={handleUpload} disabled={uploading || uploadStatus === 'success' || deleting !== null}>
                  {uploading ? <><Loader className="h-4 w-4 mr-2 animate-spin" />{t('imgUploadingShort')}</> : <><Upload className="h-4 w-4 mr-2" />{t('imgUploadBtn', { count: previewImages.length })}</>}
                </GradientButton>
              )}
            </div>
          </GlassCard>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default ImageUploadModal
